import React from 'react';
import { ArrowLeft } from 'lucide-react';

/**
 * Shared layout for SAG legal / policy pages
 * (Terms, Privacy, Refund, KYC/AML).
 *
 * Follows the same visual language as SecureCommunicationsPage /
 * SBAFinancingPage: black top bar with logo, gold accent, black
 * bottom footer strip.
 */
const LegalPageLayout = ({ eyebrow, title, lastUpdated, children, testId }) => {
  return (
    <div className="min-h-screen bg-white" data-testid={testId}>
      <header className="bg-black py-4 px-4 md:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <a
            href="/"
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="tracking-wide">Back to Home</span>
          </a>
          <img src="/logos/sag-logo.png" alt="SAG" className="h-8 w-auto" />
        </div>
      </header>

      {/* Hero */}
      <section className="py-14 md:py-20 px-4 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          {eyebrow && (
            <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase block mb-4">
              {eyebrow}
            </span>
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-4">
            {title}
          </h1>
          {lastUpdated && (
            <p className="text-gray-500 text-sm">Last updated {lastUpdated}</p>
          )}
          <div className="w-16 h-[2px] bg-[#C9A227] mx-auto mt-6" />
        </div>
      </section>

      {/* Body */}
      <section className="py-14 md:py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto legal-body">
          {children}

          <div className="mt-14 pt-8 border-t border-gray-200 text-sm text-gray-600 leading-relaxed">
            <p className="font-medium text-black mb-2">Contact us</p>
            <p>
              Sebastian Alliance Group LLC · 101 N.E. 3rd Avenue, Suite 1500,
              Fort Lauderdale, FL 33301, United States ·{' '}
              <a
                href="mailto:ecommerce@sebasalliance.net"
                className="text-[#C9A227] hover:underline"
              >
                ecommerce@sebasalliance.net
              </a>{' '}
              ·{' '}
              <a
                href="tel:+18772684779"
                className="text-[#C9A227] hover:underline"
              >
                1-877-268-4779
              </a>
            </p>
          </div>
        </div>
      </section>

      <footer className="bg-black py-6 px-4 text-center">
        <p className="text-white/40 text-xs tracking-wider">
          Sebastian Alliance Group LLC &bull; © {new Date().getFullYear()} All rights reserved
        </p>
      </footer>
    </div>
  );
};

/** Numbered section used inside a policy page */
export const LegalSection = ({ number, title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg md:text-xl font-medium text-black mb-3">
      {number ? `${number}. ` : ''}
      {title}
    </h2>
    <div className="text-gray-700 text-[15px] leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

export default LegalPageLayout;
